/**
 * ExtranetAuthAlert — bloco de alerta inline para os formulários da extranet
 * (login, cadastro, esqueci-senha, redefinir-senha).
 *
 * Renderiza um `AuthErrorInfo` retornado por `resolveAuthError()`:
 *  - Ícone conforme severidade
 *  - Título + descrição
 *  - Ação opcional (link contextualizado por tenantSlug)
 *  - correlationId no rodapé para tracking com o suporte
 */
import { Link } from 'react-router-dom';
import { AlertTriangle, Info, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AuthErrorInfo } from '@/lib/extranet/authErrors';

interface Props {
  info: AuthErrorInfo | null;
  /** Prefixo aplicado ao `href` da ação (ex: `/extranet/w2sat`). */
  hrefPrefix?: string;
  className?: string;
}

const SEVERITY_STYLES = {
  error: 'border-destructive/40 bg-destructive/5 text-destructive',
  warning: 'border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400',
  info: 'border-primary/40 bg-primary/5 text-primary',
} as const;

const ICONS = {
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
} as const;

export function ExtranetAuthAlert({ info, hrefPrefix = '', className }: Props) {
  if (!info) return null;
  const Icon = ICONS[info.severity];
  const href = info.action?.href ? `${hrefPrefix}${info.action.href}` : undefined;

  return (
    <div
      role="alert"
      className={cn(
        'rounded-md border p-3 text-sm space-y-1.5',
        SEVERITY_STYLES[info.severity],
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="flex-1 space-y-1">
          <p className="font-medium leading-snug">{info.title}</p>
          <p className="text-foreground/80 leading-snug">{info.description}</p>
          {info.action && href && (
            <Link
              to={href}
              className="inline-block pt-1 text-xs font-medium underline underline-offset-2 hover:no-underline"
            >
              {info.action.label}
            </Link>
          )}
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-foreground/50 pl-6">
        Código: {info.correlationId}
      </p>
    </div>
  );
}
